import { CTA, InputAppearanceValue, TextInput, Container, ContainerGutter } from '@volkswagen-onehub/components-core';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { validateVin } from '../../service/VinInputService/VinInputService';
import { FeatureServicesContext } from '../../use-feature-services';
import './VinInput.scss';

function VinInput({ trackingService }: Props) {
	const { t, i18n } = useTranslation('ns1', { useSuspense: false });

	const [vinNo, setVinNo] = useState('');
	const [isValid, setValid] = useState(false);
	const [loadVehicleError, setLoadVehicleError] = useState(false);
	const [errorString, setErrorString] = useState<string | undefined>(undefined);
	const [vinInputAppearance, setVinInputAppearance] = useState<InputAppearanceValue>('default');
	const featureServices = useContext(FeatureServicesContext);

	const navigate = useNavigate();

	const vinCheckTrackingCTA = () => {
		if (trackingService) {
			trackingService.track('VWBasic_CTA_Click', {
				eventInfo: {
					eventAction: 'VWBasic_CTA_Click',
					pageId: window.location.href,
					pageName: 'Start Page',
					contentId: 'startPage.vinInput.cta.checkVin',
					sectionName: 'third-section',
					brand: 'passenger',
				},
				user: {
					browserResolutionHeight: window.innerHeight,
					browserResolutionWidth: window.innerWidth,
					browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
				},
				link: {
					name: 'Check VIN',
				},
			});
		}
	};

	useEffect(() => {
		const timeOutId = setTimeout(() => {
			setLoadVehicleError(false);
			if (!featureServices) return;
			const isVinValid = validateVin(vinNo, `${featureServices['fa-content-service']?.content.vehicleManufacturerCodes}`);
			setValid(isVinValid);

			if (vinNo.length === 0) {
				setVinInputAppearance('default');
				setErrorString(undefined);
				return;
			}
			if (!isVinValid) {
				setVinInputAppearance('error');
				setErrorString(i18n.t('startPage.vinInput.invalidFormat'));
				return;
			}
			if (isVinValid) {
				setVinInputAppearance('success');
				setErrorString(undefined);
				return;
			}
			setVinInputAppearance('default');
			setErrorString(undefined);
		}, 500);
		return () => clearTimeout(timeOutId);
	}, [vinNo, featureServices, i18n]);

	useEffect(() => {
		const timeOutId = setTimeout(() => {
			if (loadVehicleError) {
				setVinInputAppearance('error');
				setErrorString(i18n.t('startPage.vinInput.vinNotFound'));
			}
		}, 500);
		return () => clearTimeout(timeOutId);
	}, [i18n, loadVehicleError]);

	useEffect(() => {
		i18n.on('languageChanged', () => {
			if (!isValid && vinNo) {
				setErrorString(i18n.t('startPage.vinInput.invalidFormat'));
			}
		});
	}, [i18n, isValid, vinNo]);

	return (
		<Container wrap="always" gutter={ContainerGutter.dynamic0050}>
			<div className="vin-input">
				<TextInput
					appearance={vinInputAppearance}
					label={t('startPage.vinInput.vin')}
					message={errorString}
					isFloating
					required
					maxLength={17}
					onChange={(event) => setVinNo(event.currentTarget.value.toUpperCase())}
				/>
			</div>

			<CTA
				stretchContent={true}
				tag="button"
				emphasis="primary"
				onClick={() => {
					vinCheckTrackingCTA();
					navigate('/vin-details', { state: { vin: vinNo } });
				}}
				disabled={!isValid}
			>
				{t('startPage.vinInput.cta.checkVin')}
			</CTA>
		</Container>
	);
}

export default VinInput;

interface Props {
	setVinOverlayActive: () => void;
	trackingService: any;
}
